
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, Eye, FileText, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import type { Candidate } from "../types";

interface ResumeSectionProps {
  candidate: Candidate;
}

interface ResumeDocument {
  id: string;
  name: string;
  url: string;
  uploadedAt: string;
  type: 'resume' | 'cover_letter' | 'portfolio' | 'certificate';
  size?: string;
}

export const ResumeSection: React.FC<ResumeSectionProps> = ({ candidate }) => {
  const [documents, setDocuments] = useState<ResumeDocument[]>([
    // Mock documents - in real app, these would come from database
    ...(candidate.resume_url ? [{
      id: '1',
      name: 'Resume.pdf',
      url: candidate.resume_url,
      uploadedAt: '2024-01-15',
      type: 'resume' as const,
      size: '245 KB'
    }] : [])
  ]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    
    try {
      // In a real app, you would upload to Supabase Storage
      // For now, we'll simulate the upload
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newDocument: ResumeDocument = {
        id: Date.now().toString(),
        name: file.name,
        url: URL.createObjectURL(file), // In real app, this would be the Supabase URL
        uploadedAt: new Date().toISOString().split('T')[0],
        type: file.name.toLowerCase().includes('cover') ? 'cover_letter' : 'resume',
        size: `${Math.round(file.size / 1024)} KB`
      };

      setDocuments(prev => [...prev, newDocument]);
      toast.success('Document uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload document');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      // Reset the input
      event.target.value = '';
    }
  };

  const handleDownload = (document: ResumeDocument) => {
    // In a real app, this would download from Supabase Storage
    window.open(document.url, '_blank');
  };

  const handleView = (document: ResumeDocument) => {
    // In a real app, this would open a document viewer
    window.open(document.url, '_blank');
  };

  const handleDelete = (documentId: string) => {
    setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    toast.success('Document deleted');
  };

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'resume': return <FileText className="h-4 w-4" />;
      case 'cover_letter': return <FileText className="h-4 w-4" />;
      case 'portfolio': return <FileText className="h-4 w-4" />;
      case 'certificate': return <FileText className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getDocumentTypeColor = (type: string) => {
    switch (type) {
      case 'resume': return 'bg-blue-100 text-blue-700';
      case 'cover_letter': return 'bg-green-100 text-green-700';
      case 'portfolio': return 'bg-purple-100 text-purple-700';
      case 'certificate': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Documents & Resume
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Section */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <Upload className="h-6 w-6 text-gray-600" />
          </div>
          <h3 className="text-lg font-medium mb-2">Upload Documents</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Upload resume, cover letters, portfolios, or certificates
          </p>
          <div className="flex justify-center">
            <label htmlFor="file-upload" className="cursor-pointer">
              <Button disabled={isUploading} asChild>
                <span>
                  <Plus className="h-4 w-4 mr-2" />
                  {isUploading ? 'Uploading...' : 'Select Files'}
                </span>
              </Button>
              <Input
                id="file-upload"
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Supported formats: PDF, DOC, DOCX (Max 5MB)
          </p>
        </div>

        {/* Documents List */}
        {documents.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium">Uploaded Documents</h4>
            {documents.map((document) => (
              <div key={document.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  {getDocumentIcon(document.type)}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{document.name}</span>
                      <Badge className={`text-xs ${getDocumentTypeColor(document.type)}`}>
                        {document.type.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Uploaded {document.uploadedAt} • {document.size}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleView(document)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDownload(document)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(document.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {documents.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No documents uploaded yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
